import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { Field, useField } from 'formik';
import { PlusIcon } from '@heroicons/react/24/outline';
import { darken, readableColor } from 'polished';

import useDebounce from '@dashboard/hooks/useDebounce';
import isHexColor from '@dashboard/services/isHexColor';

export default function ColorSelect({ name, value, onChange, presetColors = [] }) {
  const [customColor, setCustomColor] = useState(presetColors.includes(value) ? '#ffffff' : value);
  const debouncedColor = useDebounce(customColor, 100);

  useEffect(() => {
    if (debouncedColor !== value) {
      onChange(debouncedColor);
    }
  }, [debouncedColor]);

  const presetColor = presetColors.find(presetColor => presetColor.value === value);
  const isPresetColor = presetColor !== undefined;

  return (
    <fieldgroup>
      <div className="flex flex-wrap gap-1.5 max-w-xs">
        {presetColors.map(presetColor => (
          <button
            key={presetColor.value}
            type="button"
            className={clsx('h-8 w-8 rounded-full border', {
              [`ring-1 ring-offset-1`]: presetColor.value === value,
            })}
            style={{
              background: presetColor.value,
              '--tw-ring-color': darken(
                0.1,
                presetColor.type === 'solid' ? presetColor.value : presetColor.base,
              ),
              borderColor: darken(
                0.1,
                presetColor.type === 'solid' ? presetColor.value : presetColor.base,
              ),
            }}
            onClick={() => onChange(presetColor.value)}
          />
        ))}

        <Popover.Root>
          <div className="flex">
            <Popover.Trigger className="focus:outline-none block">
              <div
                className={clsx(
                  'h-8 w-8 rounded-full flex items-center justify-center ring-1 ring-gray-100 border',
                  {
                    [`ring-1 ring-offset-1`]: !isPresetColor,
                  },
                )}
                style={{
                  ...(!isPresetColor && {
                    background: customColor,
                    '--tw-ring-color': customColor ? darken(0.1, customColor) : '',
                    borderColor: customColor ? darken(0.1, customColor) : '',
                  }),
                }}
              >
                <PlusIcon
                  className={clsx('w-5 h-5', {})}
                  style={{
                    color: readableColor(
                      isHexColor(customColor) ? customColor : '#fff',
                      '#382b2f',
                      '#e0d9dd',
                      true,
                    ),
                  }}
                />
              </div>
            </Popover.Trigger>
          </div>

          <Popover.Portal>
            <Popover.Content side="bottom" align="start" className="mt-2">
              <div className="bg-gray-50 p-2 border border-gray-100 rounded w-full">
                <div className="flex flex-col">
                  <HexColorPicker
                    id={name}
                    name={name}
                    color={customColor}
                    onChange={v => {
                      if (isHexColor(v)) {
                        setCustomColor(v);
                      }
                    }}
                    style={{
                      width: '100%',
                    }}
                  />
                </div>

                <HexColorInput
                  prefixed
                  color={customColor}
                  onChange={v => {
                    if (isHexColor(v)) {
                      setCustomColor(v);
                    }
                  }}
                  className="mt-2 pl-2 block w-full border-gray-300 rounded-md font-mono text-gray-700 p-2 border"
                />
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </fieldgroup>
  );
}

export const ColorSelectField = props => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta, helpers] = useField(props);

  const { setValue } = helpers;

  const onChange = value => {
    setValue(value);

    if (props.onChange) {
      props.onChange(value);
    }
  };

  return <Field {...props} as={ColorSelect} onChange={onChange} value={field.value} />;
};
